import React from "react";
import { Checkbox, Collapse } from "antd";
import { TFilters, TSelectedFilters } from "@/interfaces/filters.interface";
import { useRouter } from "next/router";
import styles from "./collapse.module.css";
import { ArrowRightOutlined } from "@ant-design/icons";
import SubCollectionDrawer from "./SubCollectionDrawer";
import { SVG } from "@/assets/svg";

const { Panel } = Collapse;

interface IProps {
  filters: TFilters;
  selectedFilters: TSelectedFilters;
  handleSideFilterChange: (type: keyof TSelectedFilters, value: string) => void;
}

function CustomCollapse({
  filters,
  selectedFilters,
  handleSideFilterChange,
}: IProps) {
  const router = useRouter();
  const pathname = router.pathname;

  const showSubCollection = pathname.includes("my-collection");
  const [open, setOpen] = React.useState(false);
  const [activePanels, setActivePanels] = React.useState<string[]>([
    "1",
    "2",
    "3",
    "5",
  ]); // Default panels (excluding State)

  const onClose = () => {
    setOpen(false);
  };

  // Check if any country is selected
  const hasSelectedCountry = selectedFilters.country.length > 0;

  // Update active panels when country selection changes
  React.useEffect(() => {
    if (hasSelectedCountry) {
      // Add state panel if not already present
      if (!activePanels.includes("4")) {
        setActivePanels([...activePanels, "4"]);
      }
    } else {
      // Remove state panel if present
      if (activePanels.includes("4")) {
        setActivePanels(activePanels.filter((panel) => panel !== "4"));
      }
    }
  }, [hasSelectedCountry]);

  // Handle panel change (for manual expansion/collapse)
  const handlePanelChange = (keys: string | string[]) => {
    setActivePanels(Array.isArray(keys) ? keys : [keys]);
  };

  return (
    <div>
      <Collapse
        style={{ background: "none" }}
        defaultActiveKey={["1", "4"]}
        // activeKey={activePanels}
        onChange={handlePanelChange}
        expandIcon={({ isActive }) => {
          return isActive ? (
            <div style={{ position: "relative", right: "8px" }}>
              <svg
                width="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="30" height="30" rx="12" fill="white" />
                <path
                  d="M11.9199 22C17.4199 22 21.9199 17.5 21.9199 12C21.9199 6.5 17.4199 2 11.9199 2C6.41992 2 1.91992 6.5 1.91992 12C1.91992 17.5 6.41992 22 11.9199 22Z"
                  stroke="#557EFF"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.91992 12H15.9199"
                  stroke="#557EFF"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          ) : (
            <div style={{ position: "relative", right: "8px" }}>
              <svg
                width="28"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="24" height="24" rx="12" fill="white" />
                <path
                  d="M11.9199 22C17.4199 22 21.9199 17.5 21.9199 12C21.9199 6.5 17.4199 2 11.9199 2C6.41992 2 1.91992 6.5 1.91992 12C1.91992 17.5 6.41992 22 11.9199 22Z"
                  stroke="#557EFF"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.91992 12H15.9199"
                  stroke="#557EFF"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M11.9199 16V8"
                  stroke="#557EFF"
                  strokeWidth="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          );
        }}
      >
        <Panel header="Mineral Name" key="1" className="mineralHeader">
          <ul>
            {filters?.mineral.map((item, i) => (
              <li key={i} style={{ margin: "4px 0px" }}>
                <p style={{ margin: "0px 0px" }}>
                  <Checkbox
                    onChange={() =>
                      handleSideFilterChange("mineral", item.value)
                    }
                    checked={selectedFilters.mineral.includes(item.value)}
                    style={{
                      color: "#646567",
                      fontSize: "14px",
                      fontWeight: "400",
                    }}
                  >
                    {item.label} ({item.count})
                  </Checkbox>
                </p>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel header="Mine" key="2">
          {filters.rock
            .filter((el) => el.label !== "")
            .map((item, i) => (
              <ul key={i} style={{ margin: "10px 0px" }}>
                <li>
                  <p style={{ margin: "0px 0px" }}>
                    <Checkbox
                      onChange={() =>
                        handleSideFilterChange("rock", item.value)
                      }
                      checked={selectedFilters.rock.includes(item.value)}
                    >
                      {item.label} ({item.count})
                    </Checkbox>
                  </p>
                </li>
              </ul>
            ))}
          {[...filters.rock]
            .filter((el) => el.label === "")
            .map((item, i) => (
              <li key={i} style={{ margin: "10px 0px" }}>
                <p style={{ margin: "0px 0px" }}>
                  <Checkbox
                    onChange={() => handleSideFilterChange("rock", item.value)}
                    checked={selectedFilters.rock.includes(item.value)}
                  >
                    {item.label || "Others"} ({item.count})
                  </Checkbox>
                </p>
              </li>
            ))}
        </Panel>

        <Panel header="Country" key="3">
          <ul>
            {[...filters.country]
              .filter((el) => el.label !== "")
              .map((item, i) => (
                <li key={i} style={{ margin: "10px 0px" }}>
                  <p style={{ margin: "0px 0px" }}>
                    <Checkbox
                      onChange={() =>
                        handleSideFilterChange("country", item.value)
                      }
                      checked={selectedFilters.country.includes(item.value)}
                    >
                      {item.label || "Others"} ({item.count})
                    </Checkbox>
                  </p>
                </li>
              ))}
            {filters.country
              .filter((el) => el.label === "")
              .map((item, i) => (
                <li key={i} style={{ margin: "10px 0px" }}>
                  <p style={{ margin: "0px 0px" }}>
                    <Checkbox
                      onChange={() =>
                        handleSideFilterChange("country", item.value)
                      }
                      checked={selectedFilters.country.includes(item.value)}
                    >
                      {item.label || "Others"} ({item.count})
                    </Checkbox>
                  </p>
                </li>
              ))}
          </ul>
        </Panel>

        {/* State panel - only shown when country is selected */}
        {hasSelectedCountry && (
          <Panel header="State" key="4">
            <ul>
              {[...filters.state]
                .filter((el) => el.label !== "")
                .map((item, i) => (
                  <li key={i} style={{ margin: "10px 0px" }}>
                    <p style={{ margin: "0px 0px" }}>
                      <Checkbox
                        onChange={() =>
                          handleSideFilterChange("state", item.value)
                        }
                        checked={selectedFilters.state.includes(item.value)}
                      >
                        {item.label || "Others"} ({item.count})
                      </Checkbox>
                    </p>
                  </li>
                ))}
              {filters.state
                .filter((el) => el.label === "")
                .map((item, i) => (
                  <li key={i} style={{ margin: "10px 0px" }}>
                    <p style={{ margin: "0px 0px" }}>
                      <Checkbox
                        onChange={() =>
                          handleSideFilterChange("state", item.value)
                        }
                        checked={selectedFilters.state.includes(item.value)}
                      >
                        {item.label || "Others"} ({item.count})
                      </Checkbox>
                    </p>
                  </li>
                ))}
            </ul>
          </Panel>
        )}

        {showSubCollection && (
          <Panel header="Sub Collection" key="5">
            <ul>
              {[...filters.subCollection]
                .filter((el) => el.label !== "")
                .map((item, i) => (
                  <li key={i} style={{ margin: "10px 0px" }}>
                    <p style={{ margin: "0px 0px" }}>
                      <Checkbox
                        onChange={() =>
                          handleSideFilterChange("subCollection", item.value)
                        }
                        checked={selectedFilters.subCollection.includes(
                          item.value
                        )}
                      >
                        {item.label || "Others"} ({item.count})
                      </Checkbox>
                    </p>
                  </li>
                ))}
              {filters.subCollection
                .filter((el) => el.label === "")
                .map((item, i) => (
                  <li key={i} style={{ margin: "10px 0px" }}>
                    <p style={{ margin: "0px 0px" }}>
                      <Checkbox
                        onChange={() =>
                          handleSideFilterChange("subCollection", item.value)
                        }
                        checked={selectedFilters.subCollection.includes(
                          item.value
                        )}
                      >
                        {item.label || "Others"} ({item.count})
                      </Checkbox>
                    </p>
                  </li>
                ))}
            </ul>
          </Panel>
        )}
      </Collapse>
      {showSubCollection && (
        <button onClick={() => setOpen(true)} className={`${styles.print}`}>
          Manage Sub Collections
          <ArrowRightOutlined />
        </button>
      )}

      {showSubCollection && (
        <SubCollectionDrawer open={open} onClose={onClose} />
      )}
    </div>
  );
}

export default CustomCollapse;
